import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { Stack, TextField, MenuItem, Button, Radio } from '@mui/material'
import BasicModal from '@components/Modal/BasicModal'
import { useIsMobile } from '@/hooks/Responsive'
import RefreshIcon from '@mui/icons-material/Refresh'
import BoardList from '@components/Board/BoardList'
import BoardListTable from '@components/Board/BoardListTable'
import { useModalFocusManager } from '@/hooks/useModalFocusManager'
import { cmmSend, cmmCd } from 'api/comp';
import { useConfirm } from '@/components/ConfirmProvider'

export function createOrgSelectTableColumns({ selectedInstt, onSelect }) {
  return [
    {
      field: 'select',
      headerName: '선택',
      width: '64px',
      align: 'center',
      render: (_, row) => (
        <Radio
          checked={selectedInstt?.insttId === row.insttId}
          onChange={() => onSelect(row)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    { field: 'rowNum', headerName: '번호', width: '64px', align: 'center' },
    { field: 'typeNm', headerName: '유형', width: '120px', align: 'center' },
    { field: 'insttId', headerName: '기관코드', width: '120px', align: 'center' },
    { field: 'insttNm', headerName: '기관명', minWidth: '200px', align: 'left' },
    { field: 'lowestInsttNm', headerName: '하위기관명', align: 'left' },
  ]
}

export function OrgSelectToolbar({
  isMobile,
  type,
  major,
  minor,
  insttNm,
  onTypeChange,
  onMajorChange,
  onMinorChange,
  onInsttNmChange,
  onReset,
  onSearch,
  typeOptions,
  majorOptions,
  minorOptions,
}) {
  return (
    <Stack spacing={1} sx={{ width: '100%', p: 3, bgcolor: 'grey.50', borderRadius: 4, mb: 3 }}>

      {/* 1줄 : 유형/대분류/중분류 */}
      <Stack direction="row" spacing={1} sx={{ display: isMobile ? 'none' : 'flex'}}>
        <TextField
          select
          value={type}
          onChange={(e) => onTypeChange?.(e.target.value)}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">유형선택</MenuItem>
          {
          typeOptions?.map((item) => (
            <MenuItem key={item.comCd} value={item.comCd}>{item.comNm}</MenuItem>
          ))
          }
        </TextField>

        <TextField
          select
          value={major}
          onChange={(e) => onMajorChange?.(e.target.value)}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">대분류 전체</MenuItem>
          {majorOptions.map((item) => (
            <MenuItem key={item.comCd} value={item.comCd}>{item.comNm}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          value={minor}
          onChange={(e) => onMinorChange?.(e.target.value)}
          sx={{ flex: 1 }}
        >
          <MenuItem value="">중분류 전체</MenuItem>
          {minorOptions.map((item) => (
            <MenuItem key={item.comCd} value={item.comCd}>{item.comNm}</MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* 2줄 : 기관명 + 초기화 + 검색 */}
      <Stack direction="row" spacing={1}>
        <TextField
          placeholder="기관명을 입력해 주세요"
          value={insttNm}
          onChange={(e) => onInsttNmChange?.(e.target.value)}
          sx={{ flex: 1 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onSearch()
            }
          }}
        />

        <Button
          variant="outlined"
          color="grey"
          sx={{ minWidth: '48px', px: 1, display: isMobile ? 'none' : 'inline-flex' }}
          onClick={onReset}
        >
          <RefreshIcon fontSize="small" />
        </Button>

        <Button
          variant="contained"
          color="grey"
          onClick={onSearch}
          sx={{ whiteSpace: 'nowrap' }}
        >
          검색
        </Button>
      </Stack>
    </Stack>
  )
}

export default function OrgSelectModal({ open, onClose, onSelect }) {
  useModalFocusManager(open)
  const { alert } = useConfirm();
  const { isMobile } = useIsMobile()
  // 로딩 상태 추가
  const [loading, setLoading] = useState(false)
  const [openClean, setOpenClean] = useState('Y');  // 팝업시 정보 클린

  // 페이지 상태
  const [page, setPage] = useState(1)  // 현재 페이지
  const pageSize        = 5;  // 페이지 크기
  const recordSize      = 5;  // 레코드 크기
  
  const prevClean = useRef({cleanYn : 'Y'});  // 이전값
  const prevVal = useRef({type: '', major: '', minor: '', insttNm: ''});  // 이전값
  // 확정된 검색 조건 (fetcher에서 사용)
  const [confirmed, setConfirmed] = useState({
    type: '', major: '', minor: '', insttNm: ''
  })


  // 콤보정보 설정
  const [typeOptions, setTypeOptions]   = useState([]);   // 유형선택 정보
  const [majorOptions, setMajorOptions] = useState([]);   // 대분류 정보
  const [minorOptions, setMinorOptions] = useState([]);   // 중분류 정보

  // 선택된 ID
  const [selectedInstt, setSelectedInstt] = useState(null)

  // 검색 상태
  const [type, setType] = useState('AA0001');
  const [major, setMajor] = useState('');
  const [minor, setMinor] = useState('');
  const [insttNm, setInsttNm] = useState('');

  const handleSearch = () => {
    setConfirmed({ type, major, minor, insttNm })
  }

  const handleReset = () => {
    setType('AA0001');
    setMajor('');
    // setMinor('');
    setInsttNm('');
    setPage(1)  // 리셋할 때 페이지도 1로 초기화
    // setConfirmed({ type: 'AA0001', major: '', minor: '', insttNm: '' })
  }


  // 최초설정
  const init = () => {
    callType(); // 유형선택
  }
/****************************** 공통 함수 ******************************/
  // 대분류(기관유형)
  const callType = async () => {
      try {
        const resType = await cmmCd('INST_TYPE');
        setTypeOptions(resType.data);
      } catch (error) {
        console.error('기관유형 불러오기 실패:', error);
      }
  } 

  // 중분류(기관코드)
  const callMajor = async () => {
    // console.log("대분류콤보 조회:" + type + " | " + major + " | " + minor);
    try {
      const resMajor = await cmmSend('/getInsttMidList', { majorCtgy:type, midCtgy:major });
      setMajorOptions(resMajor.data);
    } catch (error) {
      console.error('기관정보 불러오기 실패:', error);
    }
  } 

  // 소분류(하위기관코드)
  const callMinor = async () => {
    // console.log("중분류콤보 조회:" + type + " | " + major + " | " + minor);
    try {
      const resMinor = await cmmSend('/getInsttSmllList', { majorCtgy:type, midCtgy:major });
      setMinorOptions(resMinor.data);
    } catch (error) {
      console.error('하위기관정보 불러오기 실패:', error);
    }
  } 
  
  /****************************** 사용자 함수 ******************************/
  const fetcher = useCallback(
    async ({ page, pageSize }) => {
      setLoading(true);
      
      if ( prevClean.current.cleanYn === "Y" ) {
        prevClean.current.cleanYn = "N";
        setLoading(false);        
        return {
          items: [],
          total: 0,
        };
      }
      
      const params = {
        page : page,
        pageSize : pageSize,
        recordSize: recordSize,
        majorCtgy : type,
        midCtgy : major,
        smllCtgy : minor,
        insttNm : insttNm,
      }

      const res = await cmmSend('/getInsttInfoList', params);
      setLoading(false)  // 데이터 로딩 끝

      return {
        items : res.data.content,
        total : res.data.totalElements
      }
    },
    [confirmed]
  )

  // 기관 선택 정보 저장  
  const handleRowSelect = useCallback((row) =>
    {
      setSelectedInstt(row);  
    }, []
  )

  // 선택 반영
  const handleConfirm = () => {
    // console.log("selectedInstt:", selectedInstt);
    if ( !selectedInstt ) {
      alert("기관이 선택되지 않았습니다.");
      return;
    }
    onSelect?.(selectedInstt);
    onClose?.();
  };

  /****************************** effect 함수 ******************************/
  /* Dialog OPEN 시 조회 조건 초기화 */
  useEffect(() => {
    // console.log("오픈:" + open);
    if ( open )
    {
      prevClean.current.cleanYn = "Y";
      setType('AA0001');
      setMajor(''); // 대분류 초기화
      setInsttNm('');
      setSelectedInstt(null);
      setConfirmed({ type: 'AA0001', major: '', minor: '', insttNm: '' })
    }
  }, [ open ]);
  
  /* 최초 설정 호출 */
  useEffect(() => {
    init();
  }, [])
  
  /* 콤보 선택시 하위 분류 조회 */
  useEffect(() => {
    if( !type || ( !type && !major ) ) {
      // console.log("유형 정보 없음");
      setMajorOptions([]);  // 중분류 초기화
      setMinorOptions([]);  // 소분류 초기화
    } else if( prevVal.current.type !== type ) {
      // console.log("유형 변경");
      callMajor();  // 대분류 조회
    } else if( prevVal.current.major !== major ) {
      // console.log("대분류 변경");
      if ( major ) {
        setMinorOptions([]);  // 소분류 초기화
        callMinor();          // 중분류 조회
      } else {
        setMinorOptions([]);  // 소분류 초기화
      }
    }

    // 변경 후 동기화
    prevVal.current = {type, major, minor};
  }, [ type, major, minor ]);
  
  // 상위 콤보가 변경되었을 경우.
  useEffect(() => {
    // 대분류 정보가 없는 경우 
    if( majorOptions.length === 0 ) {
      setMajor('');
    }
    // 중분류 정보가 없는 경우
    if( minorOptions.length === 0 ) {
      setMinor('');
    }
  }, [ majorOptions, minorOptions ]);

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title="기관 선택"
      variant="fullmodal"
      actions={
        <>
          <Button variant="outlined" color="grey" onClick={onClose}>취소</Button>
          <Button variant="contained" color="primary" onClick={handleConfirm}>등록</Button>
        </>
      }
    >
      <BoardList
        view="table"
        forceTableView
        columns={createOrgSelectTableColumns({ selectedInstt, onSelect: handleRowSelect })}
        fetcher={fetcher}
        TableComponent={BoardListTable}
        initialPageSize={pageSize}
        showPagination
        onRowClick={handleRowSelect}
        showContent={true} 
        toolbarRender={(props) => (
          <OrgSelectToolbar
            {...props}
            typeOptions={typeOptions} 
            majorOptions={majorOptions} 
            minorOptions={minorOptions}
            type={type}
            major={major}
            minor={minor}
            insttNm={insttNm}
            onTypeChange={setType}
            onMajorChange={setMajor}
            onMinorChange={setMinor}
            onInsttNmChange={setInsttNm}
            onReset={handleReset}
            onSearch={() => {
              props.handleSearch?.()
              handleSearch()
            }}
          />
        )}
      />
    </BasicModal>
  )
}